import { useState } from 'react';

import { MarkdownContent } from '@backstage/core-components';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { InfoDocDialog } from './InfoDocDialog';

const useStyles = makeStyles()(theme => ({
  container: {
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  title: {
    '&&': {
      fontWeight: 600,
    },
  },
  infoDivider: {
    '&&': {
      margin: theme.spacing(0, 0, 0, 2),
    },
  },
  infoLink: {
    cursor: 'pointer',
    '&&': {
      marginLeft: theme.spacing(2),
      fontWeight: 600,
      color: `${theme.palette.secondary.main} !important`,
    },
  },
  description: {
    fontSize: theme.typography.caption.fontSize,
    margin: 0,
    color: theme.palette.text.secondary,
    '& :first-of-type': {
      margin: 0,
      marginTop: 3,
    },
  },
}));

export interface ScaffolderInputHeaderProps {
  title: string;
  description?: string;
  variant?: 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
  /** Draws a horizontal rule under the title, for a section heading. */
  divider?: boolean;
  /** Markdown help opened in a side drawer from an `Info` link. */
  infoDoc?: string;
}

/** Title and markdown description shown above a DaaS scaffolder field. */
export const ScaffolderInputHeader = ({
  title,
  description,
  variant = 'body2',
  divider = false,
  infoDoc,
}: ScaffolderInputHeaderProps) => {
  const { classes } = useStyles();
  const [infoDocOpen, setInfoDocOpen] = useState(false);
  const showInfoDivider = Boolean(infoDoc) && !divider;

  return (
    <Box className={classes.container}>
      <Box className={classes.titleRow}>
        <Typography variant={variant} className={classes.title}>
          {title}
        </Typography>
        {showInfoDivider && (
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className={classes.infoDivider}
          />
        )}
        {infoDoc && (
          <Link
            component="button"
            type="button"
            variant="subtitle2"
            underline="hover"
            className={classes.infoLink}
            onClick={() => setInfoDocOpen(true)}
          >
            Info
          </Link>
        )}
      </Box>
      {divider && <Divider />}
      {description && (
        <MarkdownContent
          className={classes.description}
          content={description}
        />
      )}
      {infoDoc && (
        <InfoDocDialog
          dialogTitle={title}
          infoDoc={infoDoc}
          open={infoDocOpen}
          onClose={() => setInfoDocOpen(false)}
        />
      )}
    </Box>
  );
};
